import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { post_User } from '../../services/Users';
import UsernameValidatedComponent from '../../components/UsernameValidatedComponent';
import EmailAlreadyExistChecking from '../../components/EmailAlreadyExistChecking';
import { getUsername } from '../../services/UsernameChecking';
import { getEmailChecking } from '../../services/EmailChecking';
import PasswordValidation from '../../components/PasswordValidation';
import { formatUSPhone, handleUSPhoneChange } from '../../utils/phoneFormatter';
import { useUSPhoneValidation } from '../../hook/useUSPhoneValidation';
import useGroup from '../../hook/useGroup';
import Cookies from 'js-cookie';
import useConfirm from '../../hook/useConfirm';

const AddUser = () => {
    const token = Cookies.get("token-my-cdir");
    const { selectedGroupId, isSuperAdmin }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const pathRoot = isSuperAdmin ? "superadmin" : "admin";
    const location = useLocation();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState<boolean>(false);

    const [userData, setUserData] = useState<any>({
        firstName: "",
        lastName: "",
        userName: "",
        emailAddress: "",
        mobilPhoneNumber: "",
        dateFormat: "MM-dd-yyyy",
        language: "English",
        agentCompany: "",
        roleId: 2,
        groupId: ""
    });
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const {
        isValid: isPhoneValid,
        touched: phoneTouched,
        setTouched: setPhoneTouched
    } = useUSPhoneValidation(userData.mobilPhoneNumber);
    const [isUsernameChecking, setIsUsernameChecking] = useState<boolean>(false);
    const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (location.pathname.endsWith(`/${pathRoot}/user/addUser`)) {
            setShowModal(true);
        } else {
            setShowModal(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    const resetState = () => {
        setUserData({
            id: 0,
            firstName: "",
            lastName: "",
            userName: "",
            emailAddress: "",
            mobilPhoneNumber: "",
            password: "",
            dateFormat: "",
            language: "",
            agentCompany: "",
            roleId: 0,
            groupId: 0
        });
        setIsValidPassword(false);
        setEmailTouched(false);
        setUsernameTouched(false);
    };

    const closeModal = () => {
        navigate(`/${pathRoot}/user`);
        resetState();
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isUsernameChecking || isEmailChecking) return;

        if (isProcessing) return;
        setIsProcessing(true);

        if (!isPhoneValid) return;
        post_User({
            ...userData,
            password,
            groupId: selectedGroupId
        }, token!.toString())
            .then(() => {
                setUserData({
                    firstName: '',
                    lastName: '',
                    userName: '',
                    emailAddress: '',
                    mobilPhoneNumber: '',
                    dateFormat: 'MM-dd-yyyy',
                    language: 'English',
                    agentCompany: '',
                    roleId: 2,
                    groupId: 0
                });
                navigate(`/${pathRoot}/user`, { state: { refresh: true } });
                setIsProcessing(false);
            })
            .catch((error) => {
                console.error(error);
                const errorMessage = error?.response?.data || error?.message || "Something went wrong, Please try again.";
                setConfirmData({
                    title: 'Warning',
                    summary: errorMessage,
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });
                setConfirmPopup(true);
                setIsProcessing(false);
            });
    };

    return (
        <div className={`fixed inset-0 p-4 mt-14 ${showModal ? "flex" : "hidden"} flex-wrap justify-center items-center w-full h-fit z-[1000] before:fixed before:inset-0 before:w-full before:min-h-[calc(100vh-72px)] before:bg-[rgba(0,0,0,0.5)] overflow-auto`}>
            <form onSubmit={handleSubmit} className="w-full max-w-lg bg-white shadow-lg rounded-md px-5 py-4 relative">
                <svg xmlns="http://www.w3.org/2000/svg"
                    className="w-3 cursor-pointer shrink-0 fill-[#333] hover:fill-red-500 float-right" viewBox="0 0 320.591 320.591"
                    onClick={closeModal}>
                    <path
                        d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"
                        data-original="#000000"></path>
                    <path
                        d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"
                        data-original="#000000"></path>
                </svg>
                <div className="my-2 text-center">
                    <h4 className="text-xl text-secondary font-semibold">Add User</h4>
                    <div className="flex justify-between gap-2 my-4">
                        <div className="w-full text-sm">
                            <label htmlFor="firstname" className="w-full text-start block text-secondary text-xs">First Name</label>
                            <input name="firstname" type="text" required className="w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary" placeholder="Enter First Name"
                                value={userData.firstName}
                                onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                            />
                        </div>
                        <div className="w-full text-sm">
                            <label htmlFor="lastname" className="w-full text-start block text-secondary text-xs">Last Name</label>
                            <input name="lastname" type="text" required className="w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary" placeholder="Enter Last Name"
                                value={userData.lastName}
                                onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="flex justify-between gap-2 mb-4">
                        <div className="w-full text-sm">
                            <EmailAlreadyExistChecking
                                text={userData.emailAddress}
                                setText={(value) => setUserData({ ...userData, emailAddress: value })}
                                getEmailChecking={getEmailChecking}
                                className={{
                                    parentDiv: "w-full text-sm",
                                    label: "w-full text-start block text-secondary text-xs",
                                    input: "w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary",
                                }}
                                touched={emailTouched}
                                setTouched={setEmailTouched}
                                setIsChecking={setIsEmailChecking}
                            />
                        </div>

                        {
                            showModal && <UsernameValidatedComponent
                                userName={userData.userName}
                                setUserName={(value) => setUserData({ ...userData, userName: value })}
                                getUsername={getUsername}
                                className={{
                                    parentDiv: "w-full text-sm",
                                    label: "w-full text-start block text-secondary text-xs",
                                    input: "w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary",
                                }}
                                touched={usernameTouched}
                                setTouched={setUsernameTouched}
                                setIsChecking={setIsUsernameChecking}
                            />
                        }
                    </div>

                    <div className="flex justify-between gap-2 mb-4">
                        <PasswordValidation
                            password={password}
                            setPassword={setPassword}
                            isValid={isValidPassword}
                            setIsValid={setIsValidPassword}
                            touched={passwordTouched}
                            setTouched={setPasswordTouched}
                            required
                        />
                        <div className="w-full text-sm">
                            <label htmlFor="mobilPhoneNumber" className="w-full text-start block text-secondary text-xs">Phone Number</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formatUSPhone(userData.mobilPhoneNumber)}
                                onChange={(e) => {
                                    setPhoneTouched(true);
                                    handleUSPhoneChange(e, (digits) =>
                                        setUserData((prev: any) => ({
                                            ...prev,
                                            mobilPhoneNumber: digits,
                                        }))
                                    );
                                }}
                                required
                                className="w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary"
                                placeholder="Enter phone number"
                            />

                            {!isPhoneValid && phoneTouched && (
                                <p className="text-xs text-red-500 mt-1">
                                    Please enter a valid 10-digit phone number
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-between gap-2 mb-4">
                        <div className="w-full text-sm">
                            <label htmlFor="language" className="w-full text-start block text-secondary text-xs">Language</label>
                            <select name="language" required className="w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary"
                                value={userData.language}
                                onChange={(e) => setUserData({ ...userData, language: e.target.value })}
                            >
                                <option value={"English"}>English</option>
                            </select>
                        </div>
                        <div className="w-full text-sm">
                            <label htmlFor="dateFormat" className="w-full text-start block text-secondary text-xs">Date Format</label>
                            <select name="dateFormat" required className="w-full border bg-[#f0f1f2] border-gray-300 px-2 py-2 rounded outline-secondary"
                                value={userData.dateFormat}
                                onChange={(e) => setUserData({ ...userData, dateFormat: e.target.value })}
                            >
                                <option value={"MM-dd-yyyy"}>MM-dd-yyyy</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex max-xm:flex-col gap-4 !mt-4">
                    <button
                        type="button"
                        className="px-6 py-2 min-w-[150px] w-full rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                        onClick={closeModal}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-6 py-2 min-w-[150px] w-full rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue"
                        disabled={isUsernameChecking || isEmailChecking || isProcessing}
                    >
                        {isUsernameChecking || isEmailChecking || isProcessing ? "Processing..." : "Save"}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddUser